import React, {useState, useEffect} from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import Add from "@mui/icons-material/Add";
import { CrmPage, CrmPageHeader, CrmActionButton, CrmTableCard, CrmRowActions, CrmNotice, ListLink, ListTruncate } from "../shared/CrmUi";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "../../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../../examples/LayoutContainers/DashboardLayout";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js";
import Footer from "../../../../../../examples/Footer";
import { appConfirm } from "components/AppDialog";
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
);

function StrategyTable() {
    const [data, setData] = useState([]); // Backend'den gelen ham veriler
    const [tableData, setTableData] = useState([]); // Tabloya uygun hale getirilmiş veriler
    const [selectedRows, setSelectedRows] = useState([]); // Seçilen satırlar
    const [notification, setNotification] = useState({ value: false, text: "" });
    const navigate = useNavigate();

    // Backend'den veri çekme işlemi
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await CrudService.getStrategies();
                const normalizedData = response.data.map((item) => ({
                    id: item.id,
                    ...item.attributes,
                }));
                setData(normalizedData);
            } catch (error) {
                console.error("Error fetching strategies:", error);
                setNotification({
                    value: true,
                    text: "Failed to load strategies. Please try again later.",
                });
            }
        };
        fetchData();
    }, []);



    // Verileri tabloya uygun hale getiriyoruz
    const getRows = (data) => {
        return data.map((strategy) => {
            // keyMarkets yoksa boş dizi
            const markets = strategy.keyMarkets || [];

            // Continents birleştirme
            const regions = markets
                .map((m) => m.region)
                .filter((c) => c)          // boşları at
                .join(", ");

            // Total Score
            const totalScore = markets
                .reduce((sum, m) => sum + (Number(m.score) || 0), 0);

            // Incoterms
            const incoterms = markets
                .flatMap((m) => m.selectedIncoterm || [])
                .join(", ");

            // Shipping Preferences
            const shippingPrefs = markets
                .flatMap((m) => m.shippingPreferences || [])
                .join(", ");

            // Industries
            const industries = markets
                .flatMap((m) => m.industry || [])
                .join(", ");

            return {
                id: strategy.id,
                strategyName: (
                    <ListLink onClick={() => clickEditHandler(strategy.id)}>
                        <ListTruncate text={strategy.strategyName || "—"} max={60} />
                    </ListLink>
                ),
                region: regions,
                score: totalScore,
                incoterms,
                shippingPreferences: shippingPrefs,
                industries,
                actions: (
                    <CrmRowActions
                        editTitle="Edit Strategy"
                        deleteTitle="Delete Strategy"
                        onEdit={() => clickEditHandler(strategy.id)}
                        onDelete={(e) => clickDeleteHandler(e, strategy.id)}
                    />
                ),
            };
        });
    };


    useEffect(() => {
        if (data.length > 0) {
            setTableData(getRows(data)); // Veriyi tabloya uygun hale getir ve güncelle
        }
    }, [data, selectedRows]); // selectedRows'u da izleyerek tabloyu güncel tutuyoruz

    // Edit quotingTool button click handler
    const clickEditHandler = (id) => {
        navigate(`/strategy/create-strategy/details?id=${id}`); // ID'yi query parametre olarak gönderiyoruz
    };

    const clickDeleteHandler = async (e, id) => {
        try {
            if (!await appConfirm("Are you sure you want to delete this strategy?")) {
                return;
            }

            // Silme işlemi
            await CrudService.deleteStrategy(id);

            // Local state'i güncelle
            const updatedData = data.filter((item) => item.id !== id);
            setData(updatedData); // `data` güncellendiği için tablo da otomatik güncellenecek

            setNotification({
                value: true,
                text: "Strategy deleted successfully.",
            });

            // Mesajı 5 saniye sonra kaldır
            setTimeout(() => {
                setNotification({ value: false, text: "" });
            }, 5000);
        } catch (error) {
            console.error("Error deleting strategy:", error);

            setNotification({
                value: true,
                text: "Failed to delete strategy. Please try again later.",
            });

            // Mesajı 5 saniye sonra kaldır
            setTimeout(() => {
                setNotification({ value: false, text: "" });
            }, 5000);
        }
    };

// Tablonun güncellenmesi
    useEffect(() => {
        setTableData(getRows(data)); // `data` değiştikçe tabloyu günceller
    }, [data]);



    const dataTableData = {
        columns: [
            {
                Header: "Strategy",
                accessor: "strategyName",
            },
            {
                Header: "Region",
                accessor: "region",
            },
            {
                Header: "Total Score",
                accessor: "score",
            },
            {
                Header: "Incoterms",
                accessor: "incoterms",
            },
            {
                Header: "Shipping Prefs",
                accessor: "shippingPreferences",
            },
            {
                Header: "Industries",
                accessor: "industries",
            },
            {
                Header: "Actions",
                accessor: "actions",
                disableSortBy: true,
            },
        ],
        rows: tableData,
    };
    const clickAddHandler = () => {
        navigate("/strategy/create-strategy/details"); // Yeni shipment ekleme sayfasına yönlendiriyoruz
    };
    return (
        <DashboardLayout>
            <DashboardNavbar/>
            <CrmPage>
                <CrmPageHeader
                    title="Sales Strategy Cards"
                    subtitle="Target regions, scores, incoterms and industries defined for each sales strategy."
                    actions={
                        <CrmActionButton icon={<Add />} onClick={clickAddHandler}>
                            Create Strategy
                        </CrmActionButton>
                    }
                />
                {notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}
                <CrmTableCard title="Strategy List" summary={`${tableData.length} records`}>
                    <DataTable table={dataTableData} canSearch />
                </CrmTableCard>
            </CrmPage>
            <Footer/>
        </DashboardLayout>
    );
}

export default StrategyTable;
